import {
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { FormHelpers, Attributes } from 'ontraccr-common';
import * as Sentry from '@sentry/react';
import { message } from 'antd';
import axios from 'axios';

const { getLinkFieldIdMap, getChangedLinkFieldIds } = Attributes;

export default function useDynamicAttribute({
  type,
  hasInitialLoaded,
  setDynamicLoading,
  currentId,
  sections,
  responses,
  dynamicAttributeFieldMap,
  onResponsesChanged,
}) {
  const [visibleFields, setVisibleFields] = useState(new Set());
  const [data, setData] = useState({});
  const lastLinkFieldIdsRef = useRef({});

  // Get current IDs per linkField (null key = default/unlinked fields)
  const linkFieldIdMap = useMemo(
    () => getLinkFieldIdMap(dynamicAttributeFieldMap, responses, currentId),
    [dynamicAttributeFieldMap, responses, currentId],
  );

  useEffect(() => {
    if (hasInitialLoaded && dynamicAttributeFieldMap) {
      const newVisibleFields = FormHelpers.getVisibleFields({ sections, responses });
      const newFields = Array.from(newVisibleFields)
        .filter((fieldId) => !visibleFields.has(fieldId));
      const removedFields = Array.from(visibleFields)
        .filter((fieldId) => !newVisibleFields.has(fieldId));
      if (newFields.length) {
        setVisibleFields(newVisibleFields);
        const relevantData = newFields.reduce((acc, fieldId) => {
          if (data?.[fieldId]) {
            acc[fieldId] = data[fieldId];
          }
          return acc;
        }, {});
        onResponsesChanged((oldResponses) => (
          FormHelpers.parseDynamicAttributesData({
            oldResponses,
            newData: relevantData,
            dynamicAttributeFieldMap,
            shouldClear: false,
          })
        ));
      } else if (removedFields.length) {
        setVisibleFields(newVisibleFields);
      }
    }
  }, [hasInitialLoaded, sections, responses, visibleFields, data, dynamicAttributeFieldMap]);

  useEffect(() => {
    if (!hasInitialLoaded) {
      setData({});
      setVisibleFields(new Set());
      lastLinkFieldIdsRef.current = {};
    }
  }, [hasInitialLoaded]);

  useEffect(() => {
    // Dynamic Attribute
    const loadDynamicAttributes = async () => {
      const changedLinkFields = getChangedLinkFieldIds(linkFieldIdMap, lastLinkFieldIdsRef.current);
      if (!changedLinkFields.length) return;

      setDynamicLoading(true);

      // create changed items array
      const items = [];

      // Update lastLinkFieldIds
      changedLinkFields.forEach(({ linkFieldId, entityId }) => {
        lastLinkFieldIdsRef.current[linkFieldId] = entityId;
        items.push({
          id: entityId,
          linkFieldId,
        });
      });

      const results = await FormHelpers.getDynamicAttributes({
        type,
        items,
        dynamicAttributeFieldMap,
        axios,
        onError: (err) => {
          Sentry.captureException(err);
          message.error('Failed to load dynamic attributes');
        },
      });

      const resultsToApply = results.filter(({ shouldUpdate }) => shouldUpdate);

      if (!resultsToApply.length) {
        setDynamicLoading(false);
        return;
      }

      // Collect all newData and apply once
      const allNewData = resultsToApply.reduce((acc, { newData }) => (
        newData ? { ...acc, ...newData } : acc
      ), {});

      if (Object.keys(allNewData).length) {
        setData((prevData) => ({ ...prevData, ...allNewData }));
      }

      // Apply all response changes in a single update
      onResponsesChanged((oldResponses) => {
        let updatedResponses = oldResponses;
        for (const { newData, linkFieldId } of resultsToApply) { // eslint-disable-line no-restricted-syntax, max-len
          updatedResponses = FormHelpers.parseDynamicAttributesData({
            oldResponses: updatedResponses,
            newData: newData ?? {},
            dynamicAttributeFieldMap,
            shouldClear: true,
            linkFieldId,
          });
        }
        return updatedResponses;
      });

      setDynamicLoading(false);
    };

    if (!hasInitialLoaded || !dynamicAttributeFieldMap) return;
    loadDynamicAttributes();
  }, [
    hasInitialLoaded,
    dynamicAttributeFieldMap,
    linkFieldIdMap,
    type,
    setDynamicLoading,
    onResponsesChanged,
  ]);
}
